import { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { spinnerRing } from '../utils/bookingUtils';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import '../css/MapEmbed.css';

// How close the map has to get to the viewport before its iframe is created.
// Enough of a head start that the map is usually ready by the time it is
// actually on screen, without pulling it in on first paint.
const PRELOAD_MARGIN = '300px';

/**
 * A Google Maps embed that costs nothing until it is nearly on screen.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Maps embed is by far the heaviest thing the landing page loads: one
 * iframe pulls in ~40 further requests of Google's own bundles (the JS API,
 * places, geometry, Google Fonts, a static map image). Mounted with the rest
 * of the page, all of that competes with the app's own first paint over the
 * same connection, which is what made the landing page feel slow — the map
 * is not even visible while it is doing it.
 *
 * `loading="lazy"` alone is not enough: it is a hint, and browsers routinely
 * fetch a lazy iframe that sits anywhere near the initial viewport. The
 * IntersectionObserver below is the part that actually holds the request
 * back, and the attribute stays on as a second line of defence.
 *
 * Nothing here reduces the map's own weight — it still costs what it costs
 * once someone scrolls to it. What changes is WHEN: the page paints and
 * becomes usable first, and the map arrives on its own afterwards behind a
 * placeholder, instead of the two racing each other.
 *
 * @param {object} props
 * @param {string} props.src - a frameable Google Maps embed URL
 *   (getMapEmbedSrc in bookingUtils.js — never a plain maps link).
 * @param {string} [props.title] - iframe title, read by screen readers.
 * @returns {JSX.Element}
 */
function MapEmbed({ src, title = 'Map location' }) {
  const containerRef = useRef(null);
  // The iframe element does not exist until this flips, so neither do any of
  // Google's requests.
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Same watchdog every other loading state uses. A cross-origin iframe never
  // reports a failure to us — no onError fires for a blocked or unreachable
  // frame — so without a deadline a spinner over a dead embed would run for
  // as long as the page is open.
  const { timedOut } = useLoadingTimeout(inView && !loaded);

  useEffect(() => {
    if (!src || inView) return undefined;

    const node = containerRef.current;
    // No element yet, or a browser without IntersectionObserver: fall back to
    // mounting straight away. Worst case is the behaviour this component
    // replaced, never a page with no map on it.
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        setInView(true);
        // One-shot: the iframe stays mounted from here on, so there is
        // nothing left to watch for.
        observer.disconnect();
      }
    }, { rootMargin: PRELOAD_MARGIN });

    observer.observe(node);
    return () => observer.disconnect();
  }, [src, inView]);

  return (
    <div className="jt-map-embed" ref={containerRef}>
      {inView && src && (
        <iframe
          src={src}
          title={title}
          // Kept alongside the observer above, not instead of it.
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className={`jt-map-embed-frame${loaded ? ' loaded' : ''}`}
          onLoad={() => setLoaded(true)}
        />
      )}

      {/* Covers the frame until Google says it has painted, so the guest
          never sees a half-drawn map or an empty grey box. Gated on `loaded`
          alone: a map that arrives late still replaces this, because a map
          that works is always better than an apology about one that didn't. */}
      {!loaded && (
        <div className="jt-map-embed-placeholder">
          {timedOut ? (
            <>
              <MapPin size={28} />
              <span>The map couldn&rsquo;t load. Use Get Directions to open it in Google Maps.</span>
            </>
          ) : spinnerRing('Loading map…')}
        </div>
      )}
    </div>
  );
}

export default MapEmbed;
